import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomers, useSoftDeleteCustomer, type Customer } from "@/hooks/useCustomers";
import { usePermissions } from "@/hooks/usePermissions";
import { usePagination } from "@/hooks/usePagination";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { NewCustomerDialog } from "@/components/controller/NewCustomerDialog";
import { Search, Plus, Pencil, Trash2, ExternalLink, ChevronRight, FileSpreadsheet, FileText, Printer } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { EntityContextMenu } from "@/components/shared/EntityContextMenu";
import { PageActions } from "@/components/shared/PageActions";
import { useExportActions } from "@/lib/export";
import { RequirePermission } from "@/components/RequirePermission";
import { useIsMobile } from "@/hooks/use-mobile";

const TYPE_LABELS: Record<string, string> = {
  general_contractor: "General Contractor",
  property_manager: "Property Manager",
  owner: "Owner",
  insurance_company: "Insurance Co.",
  other: "Other",
};

const SYNC_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  synced: { label: "Synced", variant: "default" },
  pending: { label: "Pending", variant: "secondary" },
  error: { label: "Error", variant: "destructive" },
  not_synced: { label: "Not Synced", variant: "outline" },
};

const useCustomerProjectCounts = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customer_project_counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("customer_id, status");
      if (error) throw error;
      const counts: Record<string, { total: number; active: number }> = {};
      data?.forEach((p) => {
        if (p.customer_id) {
          if (!counts[p.customer_id]) counts[p.customer_id] = { total: 0, active: 0 };
          counts[p.customer_id].total++;
          if (p.status === "active") counts[p.customer_id].active++;
        }
      });
      return counts;
    },
    enabled: !!user,
  });
};

const CustomersInner = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const pagination = usePagination();
  const { data: customers, isLoading } = useCustomers(pagination);
  const { data: projectCounts } = useCustomerProjectCounts();
  const deleteCustomer = useSoftDeleteCustomer();
  const { can } = usePermissions();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [drawerEditOpen, setDrawerEditOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!customers) return [];
    if (!search.trim()) return customers;
    const s = search.toLowerCase();
    return customers.filter((c) =>
      c.name.toLowerCase().includes(s) ||
      c.contact_name?.toLowerCase().includes(s) ||
      c.email?.toLowerCase().includes(s)
    );
  }, [customers, search]);

  const handleDelete = async (c: Customer) => {
    try {
      await deleteCustomer.mutateAsync(c.id);
      toast.success("Customer deleted");
      setSelectedCustomer(null);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const syncBadge = (status: string | null) => {
    const s = SYNC_BADGE[status || "not_synced"] || SYNC_BADGE.not_synced;
    return <Badge variant={s.variant} className="text-[10px]">{s.label}</Badge>;
  };

  const { doExport } = useExportActions({ entityType: "customers" });
  const hasData = filtered.length > 0;

  const pageActions = [
    {
      label: "New Customer",
      icon: Plus,
      onClick: () => { setEditCustomer(null); setDialogOpen(true); },
      permission: can("manage_customers"),
    },
    {
      label: "Export to Excel",
      icon: FileSpreadsheet,
      onClick: () => hasData && doExport("excel", filtered),
      permission: hasData,
      separator: true,
    },
    {
      label: "Export to CSV",
      icon: FileText,
      onClick: () => hasData && doExport("csv", filtered),
      permission: hasData,
    },
    {
      label: "Print",
      icon: Printer,
      onClick: () => hasData && doExport("print", filtered),
      permission: hasData,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">Customers</h1>
          <PageActions actions={pageActions} />
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search customers…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : !filtered.length ? (
        <p className="text-muted-foreground">No customers found.</p>
      ) : isMobile ? (
        <div className="space-y-3">
          {filtered.map((c) => {
            const pc = projectCounts?.[c.id];
            const activeCount = pc?.active || 0;
            return (
              <Card key={c.id} className="cursor-pointer active:scale-[0.98] transition-transform" onClick={() => setSelectedCustomer(c)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{c.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-[10px]">{TYPE_LABELS[c.customer_type || "other"] || c.customer_type}</Badge>
                        {syncBadge(c.qb_sync_status)}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm text-muted-foreground">
                    <span>{c.contact_name || c.email || "No contact"}</span>
                    {activeCount > 0 ? (
                      <Badge variant="secondary" className="text-[10px]">{activeCount} active</Badge>
                    ) : (
                      <span className="text-xs">0 projects</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>QB Sync</TableHead>
                <TableHead>Projects</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => {
                const pc = projectCounts?.[c.id];
                const activeCount = pc?.active || 0;
                return (
                  <EntityContextMenu key={c.id} entityType="customers" entityId={c.id} data={c}>
                  <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedCustomer(c)}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-sm">{TYPE_LABELS[c.customer_type || "other"] || c.customer_type}</TableCell>
                    <TableCell className="text-sm">{c.contact_name || "—"}</TableCell>
                    <TableCell className="text-sm">{c.email || "—"}</TableCell>
                    <TableCell className="text-sm">{c.phone || "—"}</TableCell>
                    <TableCell>{syncBadge(c.qb_sync_status)}</TableCell>
                    <TableCell>
                      {activeCount > 0 ? (
                        <Badge
                          variant="secondary"
                          className="cursor-pointer hover:bg-primary/20"
                          onClick={(e) => { e.stopPropagation(); navigate(`/controller/customers/${c.id}?tab=projects`); }}
                        >
                          {activeCount} active
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); navigate(`/controller/customers/${c.id}`); }}
                      >
                        View <ExternalLink className="ml-1 h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  </EntityContextMenu>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {!isLoading && filtered.length > 0 && <PaginationControls pagination={pagination} />}

      {/* New / Edit Dialog */}
      <NewCustomerDialog
        open={dialogOpen || drawerEditOpen}
        onOpenChange={(v) => { setDialogOpen(v); setDrawerEditOpen(v); }}
        editCustomer={editCustomer}
        onCreated={() => {}}
      />

      {/* Detail Drawer */}
      <Sheet open={!!selectedCustomer} onOpenChange={(v) => { if (!v) setSelectedCustomer(null); }}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          {selectedCustomer && (
            <>
              <SheetHeader>
                <SheetTitle>{selectedCustomer.name}</SheetTitle>
                <SheetDescription>Customer Details</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-muted-foreground text-xs">Type</p><p>{TYPE_LABELS[selectedCustomer.customer_type || "other"]}</p></div>
                  <div><p className="text-muted-foreground text-xs">Contact</p><p>{selectedCustomer.contact_name || "—"}</p></div>
                  <div><p className="text-muted-foreground text-xs">Email</p><p>{selectedCustomer.email || "—"}</p></div>
                  <div><p className="text-muted-foreground text-xs">Phone</p><p>{selectedCustomer.phone || "—"}</p></div>
                </div>

                {(selectedCustomer.billing_address || selectedCustomer.billing_city) && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Billing Address</p>
                    <p>{[selectedCustomer.billing_address, selectedCustomer.billing_city, selectedCustomer.billing_state, selectedCustomer.billing_zip].filter(Boolean).join(", ")}</p>
                  </div>
                )}

                {(selectedCustomer.job_address || selectedCustomer.job_city) && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Job Address</p>
                    <p>{[selectedCustomer.job_address, selectedCustomer.job_city, selectedCustomer.job_state, selectedCustomer.job_zip].filter(Boolean).join(", ")}</p>
                  </div>
                )}

                {selectedCustomer.notes && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Notes</p>
                    <p>{selectedCustomer.notes}</p>
                  </div>
                )}

                <div className="border-t pt-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">QuickBooks</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">ID: {selectedCustomer.qb_customer_id || "—"}</span>
                    {syncBadge(selectedCustomer.qb_sync_status)}
                  </div>
                </div>

                <div className="border-t pt-3">
                  <p className="text-xs text-muted-foreground">Projects: {projectCounts?.[selectedCustomer.id]?.total || 0}</p>
                  <p className="text-xs text-muted-foreground">Created: {format(new Date(selectedCustomer.created_at), "MMM d, yyyy")}</p>
                </div>
              </div>

              <SheetFooter className="mt-6 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate(`/controller/customers/${selectedCustomer.id}`)}>
                  <ExternalLink className="mr-1 h-3 w-3" />Full Details
                </Button>
                {can("manage_customers") && (
                  <Button variant="outline" size="sm" onClick={() => {
                    setEditCustomer(selectedCustomer);
                    setDrawerEditOpen(true);
                  }}>
                    <Pencil className="mr-1 h-3 w-3" />Edit
                  </Button>
                )}
                {can("manage_customers") && (
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deleteCustomer.isPending}
                    onClick={() => handleDelete(selectedCustomer)}
                  >
                    <Trash2 className="mr-1 h-3 w-3" />Delete
                  </Button>
                )}
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const Customers = () => (
  <RequirePermission anyOf={["manage_customers", "view_projects"]}>
    <CustomersInner />
  </RequirePermission>
);

export default Customers;